import { Modal, Form, Input, InputNumber, Select, Space, Switch, Typography, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CODIFICATION_QUESTION_TYPE } from '../../../constant/codification';
import { TH5PQuiz } from '../../../models/courses/h5p';
import { createDefaultOption, createDefaultQuiz, extractPlaceholderIndices, sanitizeQuiz } from '../utils';
import H5PAnswerEditorList from './H5PAnswerEditorList';

interface H5PInteractionEditorProps {
  open: boolean;
  initialValue?: TH5PQuiz;
  onCancel: () => void;
  onSubmit: (quiz: TH5PQuiz) => void;
}

type QuestionType = typeof CODIFICATION_QUESTION_TYPE[keyof typeof CODIFICATION_QUESTION_TYPE];

const H5PInteractionEditor: React.FC<H5PInteractionEditorProps> = ({
  open,
  initialValue,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [quiz, setQuiz] = useState<TH5PQuiz>(createDefaultQuiz());

  const questionOptions = useMemo(
    () => [
      { value: CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE, label: t('h5p.editor.questionType.single') },
      { value: CODIFICATION_QUESTION_TYPE.MULTI_CHOICE, label: t('h5p.editor.questionType.multiple') },
      { value: CODIFICATION_QUESTION_TYPE.TEXT_INPUT, label: t('h5p.editor.questionType.text') },
      { value: CODIFICATION_QUESTION_TYPE.NUMERIC_INPUT, label: t('h5p.editor.questionType.numeric') },
      { value: CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS, label: t('h5p.editor.questionType.fill') },
      { value: CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP, label: t('h5p.editor.questionType.drag') },
    ],
    [t],
  );

  useEffect(() => {
    if (open) {
      setQuiz(initialValue ? sanitizeQuiz(initialValue) : createDefaultQuiz());
    }
  }, [initialValue, open]);

  const selectedQuestionOption = useMemo(
    () => questionOptions.find((option) => option.value === quiz.questionType),
    [questionOptions, quiz.questionType],
  );

  const handleQuestionTypeChange = (value: QuestionType) => {
    if (value === CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE) {
      const answers = quiz.answers.length > 0 ? quiz.answers : [createDefaultOption(), createDefaultOption()];
      const ensured = answers.map((answer, index) => ({ ...answer, isCorrect: index === 0 }));
      setQuiz((prev) => ({
        ...prev,
        questionType: value,
        answers: ensured,
        minValidAnswers: 1,
      }));
      return;
    }

    if (value === CODIFICATION_QUESTION_TYPE.MULTI_CHOICE) {
      const answers = quiz.answers.length > 0 ? quiz.answers : [
        { ...createDefaultOption(), isCorrect: true },
        createDefaultOption(),
      ];
      setQuiz((prev) => ({
        ...prev,
        questionType: value,
        answers,
        minValidAnswers: Math.max(1, answers.filter((answer) => answer.isCorrect).length),
      }));
      return;
    }

    if (value === CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS) {
      const answers = quiz.answers.length > 0
        ? quiz.answers.map((answer) => ({ ...answer, isCorrect: true }))
        : [{ ...createDefaultOption(), isCorrect: true }];
      setQuiz((prev) => ({
        ...prev,
        questionType: value,
        answers,
        minValidAnswers: answers.length || 1,
      }));
      return;
    }

    if (value === CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP) {
      const existingAnswers = quiz.answers.length > 0 ? quiz.answers : [];
      const hasTarget = existingAnswers.some((answer) => answer.isCorrect);
      const answers = hasTarget
        ? existingAnswers
        : [
            { ...createDefaultOption(), isCorrect: true },
            { ...createDefaultOption(), isCorrect: true },
            createDefaultOption(),
          ];
      setQuiz((prev) => ({
        ...prev,
        questionType: value,
        answers,
        minValidAnswers: Math.max(1, answers.filter((answer) => answer.isCorrect).length),
      }));
      return;
    }

    // Text input / numeric input fallback
    const answers = quiz.answers.length > 0 ? quiz.answers : [{ ...createDefaultOption(), isCorrect: true }];
    setQuiz((prev) => ({
      ...prev,
      questionType: value,
      answers: answers.slice(0, 1).map((answer) => ({ ...answer, isCorrect: true })),
      minValidAnswers: 1,
    }));
  };

  const handleSubmit = () => {
    const sanitized = sanitizeQuiz(quiz);

    if (!sanitized.question.trim()) {
      message.error(t('h5p.editor.validation.questionRequired'));
      return;
    }

    if (sanitized.time < 0) {
      message.error(t('h5p.editor.validation.timePositive'));
      return;
    }

    const placeholderCount = extractPlaceholderIndices(sanitized.question).length;

    if (
      sanitized.questionType === CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE ||
      sanitized.questionType === CODIFICATION_QUESTION_TYPE.MULTI_CHOICE
    ) {
      if (sanitized.answers.length < 2) {
        message.error(t('h5p.editor.validation.multipleAnswers'));
        return;
      }

      const correctCount = sanitized.answers.filter((answer) => answer.isCorrect).length;
      if (correctCount === 0) {
        message.error(t('h5p.editor.validation.correctAnswerRequired'));
        return;
      }

      if (
        sanitized.questionType === CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE &&
        correctCount !== 1
      ) {
        message.error(t('h5p.editor.validation.singleChoiceOnlyOne'));
        return;
      }

      sanitized.minValidAnswers = sanitized.questionType === CODIFICATION_QUESTION_TYPE.MULTI_CHOICE
        ? Math.min(correctCount, sanitized.answers.length)
        : 1;
    } else if (
      sanitized.questionType === CODIFICATION_QUESTION_TYPE.TEXT_INPUT ||
      sanitized.questionType === CODIFICATION_QUESTION_TYPE.NUMERIC_INPUT
    ) {
      if (!sanitized.answers[0]?.label.trim()) {
        message.error(t('h5p.editor.validation.expectedAnswerRequired'));
        return;
      }
      sanitized.minValidAnswers = 1;
      sanitized.answers = [
        {
          ...sanitized.answers[0],
          isCorrect: true,
        },
      ];
    } else if (sanitized.questionType === CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS) {
      if (sanitized.answers.length === 0 || sanitized.answers.some((answer) => !answer.label.trim())) {
        message.error(t('h5p.editor.validation.fillAnswerRequired'));
        return;
      }

      if (placeholderCount > 0 && placeholderCount !== sanitized.answers.length) {
        message.error(t('h5p.editor.validation.fillPlaceholderMismatch'));
        return;
      }

      sanitized.answers = sanitized.answers.map((answer) => ({ ...answer, isCorrect: true }));
      sanitized.minValidAnswers = sanitized.answers.length || 1;
    } else if (sanitized.questionType === CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP) {
      const targetAnswers = sanitized.answers.filter((answer) => answer.isCorrect);
      if (targetAnswers.length === 0) {
        message.error(t('h5p.editor.validation.dragTargetRequired'));
        return;
      }

      if (targetAnswers.some((answer) => !answer.label.trim())) {
        message.error(t('h5p.editor.validation.dragTargetLabelRequired'));
        return;
      }

      if (placeholderCount === 0) {
        message.error(t('h5p.editor.validation.dragPlaceholderRequired'));
        return;
      }

      if (placeholderCount !== targetAnswers.length) {
        message.error(t('h5p.editor.validation.dragPlaceholderMismatch'));
        return;
      }

      if (sanitized.answers.some((answer) => !answer.label.trim())) {
        message.error(t('h5p.editor.validation.dragOptionLabelRequired'));
        return;
      }

      sanitized.minValidAnswers = targetAnswers.length;
    }

    onSubmit(sanitized);
  };
  
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      width={720}
      destroyOnClose
      okText={initialValue ? t('h5p.editor.actions.update') : t('h5p.editor.actions.add')}
      cancelText={t('button.cancel')}
      centered
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Typography.Title level={5}>{t('h5p.editor.title')}</Typography.Title>
        <Form layout="vertical">
          <Form.Item label={t('h5p.editor.fields.title.label')}>
            <Input
              value={quiz.title}
              onChange={(event) => setQuiz((prev) => ({ ...prev, title: event.target.value }))}
              placeholder={t('h5p.editor.fields.title.placeholder')}
            />
          </Form.Item>
          <Form.Item label={t('h5p.editor.fields.description.label')}>
            <Input.TextArea
              value={quiz.description}
              onChange={(event) => setQuiz((prev) => ({ ...prev, description: event.target.value }))}
              placeholder={t('h5p.editor.fields.description.placeholder')}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
          <Form.Item label={t('h5p.editor.fields.time.label')} required>
            <InputNumber
              min={0}
              value={quiz.time}
              onChange={(value) => setQuiz((prev) => ({ ...prev, time: value ?? 0 }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Switch
                checked={quiz.pauseVideo}
                onChange={(checked) => setQuiz((prev) => ({ ...prev, pauseVideo: checked }))}
              />
              <Typography.Text>{t('h5p.editor.fields.pauseVideo')}</Typography.Text>
            </Space>
          </Form.Item>
          <Form.Item>
            <Space>
              <Switch
                checked={quiz.allowRetry}
                onChange={(checked) => setQuiz((prev) => ({ ...prev, allowRetry: checked }))}
              />
              <Typography.Text>{t('h5p.editor.fields.allowRetry')}</Typography.Text>
            </Space>
          </Form.Item>
          <Form.Item label={t('h5p.editor.fields.question.label')} required>
            <Input.TextArea
              value={quiz.question}
              onChange={(event) => setQuiz((prev) => ({ ...prev, question: event.target.value }))}
              placeholder={t('h5p.editor.fields.question.placeholder')}
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </Form.Item>
          <Form.Item label={t('h5p.editor.fields.questionType.label')} required>
            <Select
              value={selectedQuestionOption?.value}
              options={questionOptions}
              onChange={handleQuestionTypeChange}
            />
          </Form.Item>
          <Form.Item label={t('h5p.editor.fields.answers.label')}>
            <H5PAnswerEditorList
              questionType={quiz.questionType as QuestionType}
              answers={quiz.answers}
              onChange={(answers) =>
                setQuiz((prev) => ({
                  ...prev,
                  answers,
                }))
              }
            />
          </Form.Item>
          <Form.Item label={t('h5p.editor.fields.feedbackCorrect.label')}>
            <Input.TextArea
              value={quiz.feedbackCorrect}
              onChange={(event) => setQuiz((prev) => ({ ...prev, feedbackCorrect: event.target.value }))}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
          <Form.Item label={t('h5p.editor.fields.feedbackIncorrect.label')}>
            <Input.TextArea
              value={quiz.feedbackIncorrect}
              onChange={(event) => setQuiz((prev) => ({ ...prev, feedbackIncorrect: event.target.value }))}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
};

export default H5PInteractionEditor;